import Head from 'next/head';
import styled, { css, useTheme } from 'styled-components';

import ForwardSlashIcon from '~/components/shared/core/icons/ForwardSlashIcon';
import { FesLink } from '~/components/shared/core/link/FesLink';
import { media } from '~/components/shared/core/styles';
import { SCHEMA_CONTEXT, schemaType } from '~/utilities/constants/schemaTypes';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  Breadcrumbs: styled.div`
    font-size: 12px;
    padding: ${({ theme }) => `${theme.padding.xs} ${theme.padding.sm}`};
    background-color: ${({ theme }) => theme.colors.background.base};

    & ul {
      margin: 0;
      padding: 0;
      list-style-type: none;

      & li {
        display: inline-block;
        white-space: nowrap;
      }
    }

    & a,
    & a:visited {
      color: ${({ theme }) => theme.colors.slate['900']};
      text-decoration: none;

      ${({ theme }) =>
        getSiteStyles(theme.siteType, {
          [SiteType.Outlet]: css`
            color: ${theme.colors.content.primary};
          `,
        })}
    }

    & a:hover,
    a:focus {
      color: ${({ theme }) => theme.colors.neutral['800']};
      text-decoration: none;

      ${({ theme }) =>
        getSiteStyles(theme.siteType, {
          [SiteType.Outlet]: css`
            color: ${theme.colors.content.primary};
          `,
        })}
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      display: none;
    }
  `,
  IconWrapper: styled.span`
    display: inline-block;
    width: ${({ theme }) => theme.size['2']};
    height: ${({ theme }) => theme.size['2']};
    margin: 0 ${({ theme }) => theme.size['2']};
  `,
};

export const Breadcrumbs = () => {
  const { productionHost, siteType } = useAppContext();
  const {
    product: { breadcrumbs },
  } = usePDPContext();

  const theme = useTheme();

  const iconColor =
    siteType === SiteType.Outlet ? theme.colors.neutral[200] : theme.colors.slate[300];

  const breadcrumbsScriptData = {
    '@context': SCHEMA_CONTEXT,
    '@type': schemaType.breadcrumbList,
    itemListElement: breadcrumbs?.map((breadcrumb, index) => ({
      '@type': schemaType.listItem,
      position: index + 1,
      name: breadcrumb?.name,
      item: `${productionHost}${breadcrumb?.url}`,
    })),
  };

  return breadcrumbs ? (
    <>
      <S.Breadcrumbs data-testid="breadcrumbs">
        <ul>
          {breadcrumbs.map(
            (breadcrumb, i) =>
              breadcrumb?.url &&
              breadcrumb?.name && (
                <li key={i}>
                  <FesLink href={breadcrumb.url}>{breadcrumb.name}</FesLink>

                  {breadcrumbs.length !== i + 1 && (
                    <S.IconWrapper data-testid="forward-slash-icon">
                      <ForwardSlashIcon color={iconColor} />
                    </S.IconWrapper>
                  )}
                </li>
              )
          )}
        </ul>
      </S.Breadcrumbs>

      <Head>
        <script
          id="breadcrumbs-structured-data"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsScriptData) }}
        />
      </Head>
    </>
  ) : (
    <></>
  );
};
